import { toast } from '@heidi/design-system/toast';
import { logger, sanitizeError } from '@heidi/runtime/logger';
import { TemplateAuthorGroup } from '@heidi/schema/ml-scribe';
import { useQueryUser } from '@heidi/workspace/data';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { isEmpty } from 'lodash-es';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

// `TemplateResponseV2`, not the door's `TemplateResponse`: that one is
// `TemplateResponseV2 & ProblemBasedChartingTemplateFields`, so swapping it in
// silently widens this hook's contract by two optional fields.
import { trackTemplateOpened } from '@heidi/templates/data';
import type {
  GetTemplateCreationRecordResponse,
  TemplateResponseV2 as Template,
} from '../../../contracts';
import {
  getTemplateById,
  getTemplateCreationRecordByTemplateId,
  useMutateCreateLatestVersionControl,
  useMutateCreateTemplateCreationRecordByTemplateId,
  useMutateUpdateTemplateCreationRecord,
  useMutateUpdateVersionControl,
} from '../../../data';
import { templateDetailsMessages } from '../../template-details/templateDetails.messages';
import { isTemplateDetailsModalOpenAtom } from '../../template-details/templateDetailsModal.atoms';
import { awaitTemplatePreviewSetup } from '../../template-preview/templatePreviewExample.services';
import { useMutateGenerateExampleFromStructure } from '../../template-streaming/useMutateGenerateExampleFromStructure';
import {
  activeTabAtom,
  selectedVersionControlIdAtomFamily,
  sourceTemplateCategoryAtom,
  templateCreationRecordIdAtom,
} from '../atoms';
import { getTemplateBuilderPorts } from '../template-builder-ports';
import { templateBuilderMessages } from '../templateBuilder.messages';
import { formatCustomTemplateCopyName } from '../utils/customTemplateCopyName';
import { useQueryTemplateCreationRecordById } from './queries/useQueryTemplateCreationRecordById';

export const useHandleEditTemplate = () => {
  const { mutateAsync: createTemplateCreationRecordByTemplateId } =
    useMutateCreateTemplateCreationRecordByTemplateId();
  const { mutate: generateExample } = useMutateGenerateExampleFromStructure();
  const setTemplateCreationRecordId = useSetAtom(templateCreationRecordIdAtom);
  const setSourceTemplateCategory = useSetAtom(sourceTemplateCategoryAtom);
  const { mutateAsync: updateTemplateCreationRecord } =
    useMutateUpdateTemplateCreationRecord();
  const { mutateAsync: updateVersionControl } = useMutateUpdateVersionControl();
  const store = useStore();
  const queryClient = useQueryClient();

  const isTemplateDetailsModalOpen = useAtomValue(isTemplateDetailsModalOpenAtom);

  const { mutateAsync: createLatestVersionControl } =
    useMutateCreateLatestVersionControl();

  const setActiveTab = useSetAtom(activeTabAtom);

  const templateCreationRecordId = useAtomValue(templateCreationRecordIdAtom);
  const selectedVersionControlId = useAtomValue(
    selectedVersionControlIdAtomFamily(templateCreationRecordId ?? '')
  );

  const intl = useIntl();

  const { data: templateCreationRecord } = useQueryTemplateCreationRecordById({
    templateCreationRecordId: templateCreationRecordId ?? '',
    select: (data) => ({
      transcript: data.template_creation_record.transcript,
      example:
        data.version_controls.find(
          (versionControl) => versionControl.id === selectedVersionControlId
        )?.example_note ?? '',
      templateId: data.template_creation_record.template_id,
    }),
  });

  const { data: template } = getTemplateBuilderPorts().useQueryTemplateById(
    templateCreationRecord?.templateId,
    null
  );

  const { data: { preferredTab = 'example', userRefId } = {} } = useQueryUser((data) => ({
    preferredTab: data.template_creation_record?.preferred_tab,
    userRefId: data.user_ref_id,
  }));

  const router = getTemplateBuilderPorts().useRouter();
  const isFromMyTemplates = router.pathname.startsWith('/scribe/my-templates');
  const initializeTemplateCreationRecordAndGenerateExample = useCallback(
    ({
      templateCreationRecordId,
      selectedVersionControlId,
      isCurrent,
    }: {
      templateCreationRecordId: string;
      selectedVersionControlId: string;
      isCurrent?: () => boolean;
    }) => {
      if (isCurrent && !isCurrent()) return;
      setTemplateCreationRecordId(templateCreationRecordId);

      // Set the selected version control ID using the atom family
      store.set(
        selectedVersionControlIdAtomFamily(templateCreationRecordId),
        selectedVersionControlId
      );

      generateExample({
        template_creation_record_id: templateCreationRecordId,
        version_control_id: selectedVersionControlId,
        animationType: 'oldTemplate',
      });
    },
    [generateExample, setTemplateCreationRecordId, store]
  );

  /**
   * Handles editing a custom template copy.
   * Necessary when cloning Heidi template or team template.
   */
  const handleEditCustomTemplate = useCallback(
    async ({
      template,
      exampleNote,
      transcript,
      isCurrent,
    }: {
      template?: Template;
      exampleNote?: string;
      transcript?: string;
      isCurrent?: () => boolean;
    }) => {
      if (!template || (isCurrent && !isCurrent())) {
        return;
      }
      const customTemplateName = formatCustomTemplateCopyName(intl, template.name);
      const {
        data: {
          template_creation_record: templateCreationRecord,
          version_controls: versionControls,
        },
      } = await createTemplateCreationRecordByTemplateId(template.id);
      const selectedVersionControlId = versionControls.at(-1)?.id ?? '';
      const templateCreationRecordId = templateCreationRecord.id ?? '';
      await updateTemplateCreationRecord({
        templateCreationRecordId: templateCreationRecord.id ?? '',
        data: {
          template_name: customTemplateName,
          selected_version_control_id: selectedVersionControlId,
          transcript,
        },
      });
      if (isCurrent && !isCurrent()) return;
      // Preserve the source template's category so it can be used when saving the custom copy
      setSourceTemplateCategory(template.template_category);
      if (template.template_author_group === TemplateAuthorGroup.Team) {
        void updateVersionControl({
          templateCreationRecordId,
          versionControlId: selectedVersionControlId,
          data: {
            example_note: exampleNote ?? undefined,
          },
        });
      } else {
        initializeTemplateCreationRecordAndGenerateExample({
          templateCreationRecordId,
          selectedVersionControlId,
          isCurrent,
        });
      }

      return templateCreationRecord;
    },
    [
      createTemplateCreationRecordByTemplateId,
      initializeTemplateCreationRecordAndGenerateExample,
      intl,
      setSourceTemplateCategory,
      updateTemplateCreationRecord,
      updateVersionControl,
    ]
  );

  /**
   * Handles editing a custom template copy and navigates to the new template.
   * Used when a team member edits a team template and needs to create a personal copy.
   */
  const handleEditCustomTemplateAndNavigate = useCallback(
    async ({
      exampleNote,
      onBeforeNavigate,
      onAfterNavigate,
    }: {
      exampleNote: string;
      onBeforeNavigate: () => void;
      onAfterNavigate: () => void;
    }) => {
      toast({
        title: intl.formatMessage(templateBuilderMessages.cannotEditSharedTemplate),
      });
      const newTemplateCreationRecord = await handleEditCustomTemplate({
        template,
        exampleNote: exampleNote,
        transcript: templateCreationRecord?.transcript ?? undefined,
      });
      onBeforeNavigate();
      if (!isTemplateDetailsModalOpen) {
        await router.push(
          `/scribe/my-templates/template-creation-record/${newTemplateCreationRecord?.id}`
        );
      }
      setTemplateCreationRecordId(newTemplateCreationRecord?.id ?? '');
      onAfterNavigate();
    },
    [
      handleEditCustomTemplate,
      isTemplateDetailsModalOpen,
      router,
      setTemplateCreationRecordId,
      intl,
      template,
      templateCreationRecord?.transcript,
    ]
  );

  /**
   * Handles opening a template that already has an associated template creation record.
   *
   * This function performs the following:
   * 1. Syncs the template name if it has changed externally
   * 2. Detects if the template structure has diverged from the stored version control
   *    - If different, creates a new version control with the updated structure
   * 3. Generates an example note if the current version control doesn't have one
   * 4. Sets up the global state (template creation record ID and selected version control)
   *
   * If no changes are detected and an example already exists, it simply initializes state
   * and allows navigation without regenerating anything.
   */
  const handleEditTemplateWithTemplateCreationRecord = useCallback(
    async ({
      templateCreationRecordData,
      template,
      isCurrent,
    }: {
      templateCreationRecordData: GetTemplateCreationRecordResponse;
      template: Template;
      isCurrent?: () => boolean;
    }) => {
      if (isCurrent && !isCurrent()) return;
      const {
        template_creation_record: templateCreationRecord,
        version_controls: versionControls,
      } = templateCreationRecordData;
      const templateCreationRecordId = templateCreationRecord.id ?? '';
      let selectedVersionControlId =
        templateCreationRecord.selected_version_control_id ??
        versionControls.at(-1)?.id ??
        '';

      let selectedVersionControl = versionControls.find(
        (vc) => vc.id === selectedVersionControlId
      );

      // Check if the template structure has changed since the last version control was created
      const isStructureDifferent =
        selectedVersionControl &&
        template.structure_template !== selectedVersionControl.template_structure;

      // Check if the template name was updated externally (e.g., via settings)
      const isTemplateNameDifferent =
        template.name !== templateCreationRecord.template_name;

      // Sync template name if it has changed. The sync runs in the background
      // and must not block opening the editor: the mutation's onError already
      // logs a sanitized report and re-fetches the record, so the rejection is
      // settled here instead of reaching the global handler (DSK-1315).
      if (isTemplateNameDifferent) {
        updateTemplateCreationRecord({
          templateCreationRecordId: templateCreationRecordId,
          data: {
            template_name: template.name,
          },
        }).catch(() => undefined);
      }

      // If structure changed, create a new version control to track the updated structure
      if (isStructureDifferent) {
        const versionControl = await createLatestVersionControl({
          data: {
            template_structure: template.structure_template,
          },
          templateCreationRecordId,
        });

        selectedVersionControlId = versionControl.id;
        selectedVersionControl = versionControl;

        await updateTemplateCreationRecord({
          templateCreationRecordId: templateCreationRecordId,
          data: {
            selected_version_control_id: versionControl.id,
          },
        });
      }

      if (isCurrent && !isCurrent()) return;
      // Generate example note if the current version control doesn't have one
      if (isEmpty(selectedVersionControl?.example_note)) {
        generateExample({
          template_creation_record_id: templateCreationRecordId,
          version_control_id: selectedVersionControlId,
          animationType: 'edit',
        });
      }

      // Initialize global state for the template editor
      setTemplateCreationRecordId(templateCreationRecordId);
      store.set(
        selectedVersionControlIdAtomFamily(templateCreationRecordId),
        selectedVersionControlId
      );

      return templateCreationRecord;
    },
    [
      createLatestVersionControl,
      generateExample,
      setTemplateCreationRecordId,
      store,
      updateTemplateCreationRecord,
    ]
  );

  /**
   * Handles opening a legacy template that doesn't have an associated template creation record.
   *
   * This is for backwards compatibility with templates created before the template creation
   * record system was introduced. It:
   * 1. Creates a new template creation record linked to the existing template
   * 2. Sets up the initial version control with the template's current structure
   * 3. Updates the record with the template name and selected version control
   * 4. Generates an example note for the template
   *
   * After this, the template will have a proper template creation record and subsequent
   * edits will go through `handleEditTemplateWithTemplateCreationRecord`.
   */
  const handleEditTemplateWithoutTemplateCreationRecord = useCallback(
    async (template: Template, isCurrent?: () => boolean) => {
      if (isCurrent && !isCurrent()) return;
      // Create a new template creation record for this legacy template
      const {
        data: {
          template_creation_record: templateCreationRecord,
          version_controls: versionControls,
        },
      } = await createTemplateCreationRecordByTemplateId(template.id);
      const selectedVersionControlId = versionControls.at(-1)?.id ?? '';
      const templateCreationRecordId = templateCreationRecord.id ?? '';

      // Initialize the record with template metadata
      await updateTemplateCreationRecord({
        templateCreationRecordId: templateCreationRecord.id ?? '',
        data: {
          template_name: template.name,
          selected_version_control_id: selectedVersionControlId,
          template_id: template.id,
        },
      });

      if (isCurrent && !isCurrent()) return;
      // Set up global state and generate an example note
      initializeTemplateCreationRecordAndGenerateExample({
        templateCreationRecordId,
        selectedVersionControlId,
        isCurrent,
      });

      return templateCreationRecord;
    },
    [
      createTemplateCreationRecordByTemplateId,
      initializeTemplateCreationRecordAndGenerateExample,
      updateTemplateCreationRecord,
    ]
  );

  // A request-lifetime predicate prevents obsolete reads from changing editor state.
  // Complete durable write pairs before checking it, so created records stay linked.
  const handleEditTemplate = useCallback(
    async (template: Template, isCurrent?: () => boolean, trackOpen = true) => {
      if (isCurrent && !isCurrent()) return;
      // Direct editor initialization retains its existing tracking. Navigation
      // handoffs defer the event until the route has successfully opened.
      if (trackOpen) {
        trackTemplateOpened({
          templateId: template.id,
          templateAuthorGroup: template.template_author_group ?? TemplateAuthorGroup.User,
          version: 'v2',
        });
      }

      if (template.template_author_group === TemplateAuthorGroup.Heidi) {
        try {
          const templateCreationRecord = await handleEditCustomTemplate({
            template,
            isCurrent,
          });
          if (isCurrent && !isCurrent()) return;
          setActiveTab(isFromMyTemplates ? preferredTab : 'structure');
          return templateCreationRecord;
        } catch (error) {
          if (isCurrent && !isCurrent()) return;
          logger.error('[TEMPLATE_OPEN] prepare failed', {
            template_id: template.id,
            error: sanitizeError(error),
          });
          toast({
            tone: 'destructive',
            title: intl.formatMessage(templateBuilderMessages.errorEditingTemplate),
            description:
              error instanceof Error
                ? error.message
                : intl.formatMessage(templateDetailsMessages.errorUnknown),
          });
          return;
        }
      }

      try {
        const { data: templateCreationRecordData } =
          await getTemplateCreationRecordByTemplateId(template.id);
        if (isCurrent && !isCurrent()) return;

        const templateCreationRecord = await handleEditTemplateWithTemplateCreationRecord(
          {
            templateCreationRecordData,
            template,
            isCurrent,
          }
        );
        if (isCurrent && !isCurrent()) return;
        setActiveTab(isFromMyTemplates ? preferredTab : 'structure');
        return templateCreationRecord;
      } catch (error) {
        if (isCurrent && !isCurrent()) return;
        if (isAxiosError(error) && error.response?.status === 404) {
          const templateCreationRecord =
            await handleEditTemplateWithoutTemplateCreationRecord(template, isCurrent);
          if (isCurrent && !isCurrent()) return;
          setActiveTab(isFromMyTemplates ? preferredTab : 'structure');
          return templateCreationRecord;
        } else {
          logger.error('[TEMPLATE_OPEN] prepare failed', {
            template_id: template.id,
            error: sanitizeError(error),
          });
          toast({
            tone: 'destructive',
            title: intl.formatMessage(templateBuilderMessages.errorEditingTemplate),
            description:
              error instanceof Error
                ? error.message
                : intl.formatMessage(templateDetailsMessages.errorUnknown),
          });
        }
      }
    },
    [
      handleEditCustomTemplate,
      handleEditTemplateWithTemplateCreationRecord,
      handleEditTemplateWithoutTemplateCreationRecord,
      intl,
      isFromMyTemplates,
      preferredTab,
      setActiveTab,
    ]
  );

  /**
   * Refreshes the saved template, prepares or links its creation record and selects
   * the editor version as needed, then navigates to that record.
   * Returns true only when navigation succeeds; obsolete requests return false
   * without logging or showing an error toast.
   */
  const handleViewTemplateById = useCallback(
    async (templateId: string, isCurrent?: () => boolean): Promise<boolean> => {
      if (isCurrent && !isCurrent()) return false;
      try {
        // Fetch fresh saved content, even when the preview has cached data.
        const { data: latestTemplate } = await getTemplateById(templateId);
        if (isCurrent && !isCurrent()) return false;
        // Finish an already-started durable preview link before the record lookup
        // can interpret the not-yet-linked setup as a missing legacy record.
        if (userRefId)
          await awaitTemplatePreviewSetup(queryClient, templateId, userRefId);
        if (isCurrent && !isCurrent()) return false;
        const record = await handleEditTemplate(latestTemplate, isCurrent, false);
        if (isCurrent && !isCurrent()) return false;
        if (!record?.id) {
          if (record)
            logger.error('[TEMPLATE_OPEN] invalid record', { template_id: templateId });
          return false;
        }
        const opened = await router.push(
          `/scribe/my-templates/template-creation-record/${record.id}`
        );
        if (!opened || (isCurrent && !isCurrent())) return false;
        trackTemplateOpened({
          templateId: latestTemplate.id,
          templateAuthorGroup:
            latestTemplate.template_author_group ?? TemplateAuthorGroup.User,
          version: 'v2',
        });
        return true;
      } catch (error) {
        if (isCurrent && !isCurrent()) return false;
        logger.error('[TEMPLATE_OPEN] open failed', {
          template_id: templateId,
          error: sanitizeError(error),
        });
        toast({
          tone: 'destructive',
          title: intl.formatMessage({
            defaultMessage: "Couldn't open the template",
            id: '2B03wb1BDA',
          }),
        });
        return false;
      }
    },
    [handleEditTemplate, router, intl, queryClient, userRefId]
  );

  return {
    handleEditTemplate,
    handleEditCustomTemplateAndNavigate,
    handleViewTemplateById,
  };
};
